// src/WarrantyList.js
import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

// --- 1. CORE API & UTILS (KEEPING ALL ORIGINAL LOGIC) ---

async function apiFetch(path, { method = "GET", body } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error("Missing ID token (please sign out/in again)");

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function prettyTime(epochSeconds) {
  if (!epochSeconds) return "-";
  try {
    return new Date(Number(epochSeconds) * 1000).toLocaleString([], { 
      dateStyle: "medium", 
      timeStyle: "short" 
    });
  } catch {
    return String(epochSeconds);
  }
}

function normUpper(v) {
  return String(v || "").trim().toUpperCase();
}

/** ---------- Workflow derive logic (Unchanged) ---------- **/

function deriveWorkflowStatusLikeTab(wr) {
  const server = normUpper(wr?.derivedWorkflowStatus);
  if (server) return server;
  const ws = normUpper(wr?.workflowStatus);
  if (ws) return ws;

  const submitStatus = normUpper(wr?.submitStatus || "DRAFT");
  const approvalStatus = normUpper(wr?.approvalStatus || "");
  const finalStatus = normUpper(wr?.finalStatus || "");

  if (submitStatus !== "SUBMITTED") return "DRAFT";
  if (approvalStatus === "REJECTED") return "REJECTED_BY_APPROVER";
  if (approvalStatus !== "APPROVED") return "PENDING_APPROVER";

  const hasSalesAdmin1 = !!wr?.salesAdminSubmittedAt || normUpper(wr?.salesAdminStatus) === "SUBMITTED";
  if (!hasSalesAdmin1) return "PENDING_SALES_ADMIN_1";

  const hasLogistics = !!wr?.logisticsSubmittedAt || normUpper(wr?.logisticsStatus) === "SUBMITTED";
  if (!hasLogistics) return "PENDING_LOGISTICS";

  const hasFinal = !!wr?.finalSubmittedAt || finalStatus === "DONE";
  if (!hasFinal) return "PENDING_SALES_ADMIN_2";

  const fps = wr?.finalPdfStatus || {};
  if (normUpper(fps?.purple) === "PENDING") return "COMPLETING";

  return "COMPLETED";
}

function stepsFromWorkflow(ws) {
  const allPending = { agent: "PENDING", approver: "PENDING", salesAdmin1: "PENDING", logistics: "PENDING", salesAdminFinal: "PENDING" };
  const done1 = { ...allPending, agent: "DONE" };
  const done2 = { ...done1, approver: "DONE" };
  const done3 = { ...done2, salesAdmin1: "DONE" };
  const done4 = { ...done3, logistics: "DONE" };
  const done5 = { ...done4, salesAdminFinal: "DONE" };

  switch (ws) {
    case "DRAFT": return { ...allPending, agent: "IN_PROGRESS" };
    case "PENDING_APPROVER": return { ...done1, approver: "IN_PROGRESS" };
    case "REJECTED_BY_APPROVER": return { ...done1, approver: "REJECTED" };
    case "PENDING_SALES_ADMIN_1": return { ...done2, salesAdmin1: "IN_PROGRESS" };
    case "PENDING_LOGISTICS": return { ...done3, logistics: "IN_PROGRESS" };
    case "PENDING_SALES_ADMIN_2": return { ...done4, salesAdminFinal: "IN_PROGRESS" };
    case "COMPLETING": return { ...done4, salesAdminFinal: "IN_PROGRESS" };
    case "COMPLETED": return done5;
    default: return allPending;
  }
}

// --- 2. REFINED UI COMPONENTS ---

function Pill({ text }) {
  const t = String(text || "").toUpperCase();
  const map = {
    DONE: { bg: "#f0fdf4", fg: "#15803d", bd: "#bbf7d0", label: "Done" },
    IN_PROGRESS: { bg: "#eff6ff", fg: "#1d4ed8", bd: "#bfdbfe", label: "In Progress" },
    PENDING: { bg: "#f8fafc", fg: "#64748b", bd: "#e2e8f0", label: "Pending" },
    REJECTED: { bg: "#fef2f2", fg: "#991b1b", bd: "#fecaca", label: "Rejected" },
    NA: { bg: "#f8fafc", fg: "#94a3b8", bd: "#e2e8f0", label: "N/A" },
  };
  const c = map[t] || map.PENDING;

  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: "12px",
      border: `1px solid ${c.bd}`,
      background: c.bg,
      color: c.fg,
      fontSize: "11px",
      fontWeight: "600",
      whiteSpace: "nowrap",
    }}>
      {c.label}
    </span>
  );
}

// --- 3. MAIN COMPONENT ---

export default function WarrantyList() {
  const [bucket, setBucket] = useState("current"); 
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);

  async function load() {
    setBusy(true);
    setError("");
    try {
      const resp = await apiFetch(`/warranty/list?bucket=${encodeURIComponent(bucket)}&limit=100`);
      setItems(Array.isArray(resp?.items) ? resp.items : []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
  }, [bucket]);

  const rows = useMemo(() => {
    return (items || []).map((it) => {
      const ws = deriveWorkflowStatusLikeTab(it);
      const step = stepsFromWorkflow(ws);
      return {
        ...it,
        derivedWorkflowStatus: ws,
        step_agent: step.agent,
        step_approver: step.approver,
        step_salesAdmin1: step.salesAdmin1,
        step_logistics: step.logistics,
        step_salesAdminFinal: step.salesAdminFinal,
      };
    });
  }, [items]);

  function openWarranty(it) {
    const incidentId = it?.incidentId;
    if (!incidentId) return;
    window.location.href = `/?incidentId=${encodeURIComponent(incidentId)}#warranty`;
  }

  const s = {
    container: { padding: "32px", maxWidth: "1600px", margin: "0 auto", fontFamily: '-apple-system, sans-serif' },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" },
    title: { fontSize: "24px", fontWeight: "700", color: "#1e293b", margin: 0 },
    btnBase: { padding: "10px 16px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "white", fontWeight: "600", cursor: "pointer", fontSize: "14px", transition: "all 0.2s" },
    toggleContainer: { display: "flex", gap: "4px", background: "#e2e8f0", padding: "4px", borderRadius: "10px" },
    tableContainer: { border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
    th: { textAlign: "left", padding: "12px 16px", fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Warranty Workflows</h1>
          <p style={{ color: "#64748b", fontSize: "14px", marginTop: "4px" }}>Track processing steps across departments</p>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={s.toggleContainer}>
            {[["Current", "current"], ["Closed", "closed"]].map(([label, key]) => (
              <button
                key={key}
                onClick={() => setBucket(key)}
                style={{
                  padding: "6px 16px",
                  borderRadius: "8px",
                  border: "none",
                  background: bucket === key ? "white" : "transparent",
                  color: bucket === key ? "#0f172a" : "#64748b",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                  boxShadow: bucket === key ? "0 1px 2px rgba(0,0,0,0.1)" : "none"
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <button onClick={load} disabled={busy} style={s.btnBase}>
            {busy ? "Updating..." : "↻ Refresh"}
          </button>
        </div>
      </div>

      {error && <div style={{ color: "#b91c1c", background: "#fef2f2", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid #fee2e2" }}>{error}</div>}

      <div style={s.tableContainer}>
        <div style={{ padding: "16px", borderBottom: "1px solid #e2e8f0", fontWeight: "600", color: "#334155" }}>
          {bucket === "current" ? "Active Pipelines" : "Historical Records"} 
          <span style={{ color: "#94a3b8", fontWeight: "400", marginLeft: "8px" }}>({rows.length})</span>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <thead>
              <tr>
                {["Warranty Details", "Agent", "Approver", "Sales Admin", "Logistics", "Final Admin", "Updated"].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {busy ? (
                <tr><td colSpan={7} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading pipeline data...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>No warranty records found.</td></tr>
              ) : (
                rows.map((it) => {
                  const id = `${it.incidentId}::${it.wrId}`;
                  const isHovered = hoveredRow === id;

                  return (
                    <tr 
                      key={id} 
                      onClick={() => openWarranty(it)}
                      onMouseEnter={() => setHoveredRow(id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{ 
                        borderBottom: "1px solid #f1f5f9", 
                        cursor: "pointer",
                        background: isHovered ? "#f8fafc" : "transparent",
                        transition: "background 0.2s"
                      }}
                    >
                      <td style={{ padding: "16px" }}>
                        <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>{it.wrId || "No ID"}</div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>{it.reportNo || it.incidentId}</div>
                        <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "4px", textTransform: "uppercase", letterSpacing: "0.02em" }}>
                          {it.derivedWorkflowStatus}
                        </div>
                      </td>

                      <td style={{ padding: "16px" }}><Pill text={it.step_agent} /></td>
                      <td style={{ padding: "16px" }}><Pill text={it.step_approver} /></td>
                      <td style={{ padding: "16px" }}><Pill text={it.step_salesAdmin1} /></td>
                      <td style={{ padding: "16px" }}><Pill text={it.step_logistics} /></td>
                      <td style={{ padding: "16px" }}><Pill text={it.step_salesAdminFinal} /></td>

                      <td style={{ padding: "16px", fontSize: "12px", color: "#64748b", whiteSpace: "nowrap" }}>
                        {prettyTime(it.updatedAt)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: "20px", fontSize: "12px", color: "#94a3b8", display: "flex", justifyContent: "space-between" }}>
        <span>Tip: Click any row to view full details and manage steps.</span>
        <span>Pipeline View v2.0</span>
      </div>
    </div>
  );
}